import ExtendedPath from "csi/core/ExtendedPath";
import Key from "csi/core/Key";
import Spec from "csi/core/Spec";
import SpecBuilder from "csi/core/SpecBuilder";
import csi from "csi/main";
import { organizeComponentItems } from "csi/middleware/common";
import { RemoveEventHandle } from "csi/typings";
import { ComponentItem } from "designSystem/types/component-item-types";
import { isEmpty, sortBy } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { C8AttributeKey } from "src/c8-platform/type/value";
import { getCustomViewAvailablePaths, populateCustomViewOptionalAttributesMaps } from "../customViewService";
import CustomViewAttributeContainer from "./CustomViewAttributeContainer";
import CustomViewAttributesStore, { AllOption } from "./CustomViewAttributesStore";

export default class CustomViewAttributesLevel {
	/** attribute containers available in view */
	readonly containers = observable.array<CustomViewAttributeContainer>([], { deep: false });

	/** indicates if source type and path hints duplicate that of another level */
	readonly duplicateLevel: boolean;

	/** indicates if this level is the first level in the view */
	readonly isMainLevel: boolean;

	/** name */
	readonly name: string;

	/** indicates matrix group container when level is used to display available attributes within a matrix group */
	readonly sourceMatrixContainer?: CustomViewAttributeContainer;

	/** spec builder for attributes in view */
	readonly specBuilder: SpecBuilder;

	/** parent attributes store */
	readonly store: CustomViewAttributesStore;

	/** handle to stop populating of attributes */
	#populateHandle?: RemoveEventHandle;

	/** map of specs by attribute key */
	#specMap: Record<C8AttributeKey, Spec> = {};

	/** return containers in level that match search text, if any */
	@computed get displayContainers(): CustomViewAttributeContainer[] {
		const { store: { selectedContainer } } = this;
		return this.containers.filter((c) => (!c.filteredOut && (selectedContainer.id === AllOption.id || c.uniqueId === selectedContainer.id)));
	}

	/** first container in level */
	get firstContainer(): CustomViewAttributeContainer {
		return this.containers[0];
	}

	/** selected container in current level */
	get selectedContainer (): CustomViewAttributeContainer {
		const { containers, store: { selectedContainer } } = this;
		const selected = containers.find((container) => container.uniqueId === selectedContainer.id);
		return selected || containers[0];
	}

	/** indicates if at least one container is expanded */
	get isContainerExpanded(): boolean {
		return this.containers.some(({ expanded }) => expanded);
	}

	/** return all attributes within containers in this level except the attributes that are already selected in group setting */
	groupingAttributes(key1: string, key2: string, extraSpec?: Spec): ComponentItem[] {
		const componentItems = [];
		const add = (spec: Spec): void => {
			// skip specs that are already selected in group settings
			const isNotSelectedInGroupSettings = spec.uniqueId !== key1 && spec.uniqueId != key2;
			if (isNotSelectedInGroupSettings) {
				componentItems.push(this.store.convertSpecToComponentItemForCustomView(spec));
			}
		};

		this.containers.forEach((container) => {
			container.populate();
		});

		this.containers.forEach(({ attributeMapForGroupingAttributes }) => {
			for (const key in attributeMapForGroupingAttributes) {
				const spec = attributeMapForGroupingAttributes[key].spec;
				add(spec);
			}
		});

		if (extraSpec) {
			add(extraSpec);
		}

		return organizeComponentItems(componentItems);
	}

	/** return spec for given key */
	createAttributeSpec = (key: Key, params?: Partial<Spec>, usedHeadingKeys?: Set<string>, ignoreMatrixGroup?: boolean): Spec => {
		// maintain map of all specs generated for quick access
		// TODO - should this be moved to SpecBuilder?
		const mapKey = key.toString();
		let spec = this.#specMap[mapKey];

		if (!spec || !ignoreMatrixGroup) {
			spec = this.specBuilder.getSpecForKey(key, params, usedHeadingKeys, null, ignoreMatrixGroup);

			// don't cache matrix group keys as they can conflict with simple list keys
			if (spec && !spec.isMatrixGroup) {
				this.#specMap[mapKey] = spec;
			}
		} else {
			usedHeadingKeys?.add(spec.headingKey);
		}

		return spec;
	};
	
	/** collapse all containers */
	@action collapseAll = (): void => {
		this.containers.forEach((container) => {
			container.expanded = false;
		});
	};

	/** set search text for attribute specs */
	@action searchLevel = (text: string): void => {
		if (text) {
			// iterate through all containers and populate specs, but do so one at a time with timeout in between
			// to allow UI to update
			this.#populateHandle = csi.forEachWithTimeout(this.containers, 1, null, (container) => container.populate(true));
		} else {
			// make sure to stop populating if still not done
			this.#populateHandle?.remove();
			this.#populateHandle = null;

			// reset all groups to collapsed except for first
			this.containers.forEach((container, i) => {
				container.expanded = !i;
			});
		}
	};

	/** add containers for each concrete class of the given extended path if its end class is abstract */
	#addDerivedContainers = (extendedPath: ExtendedPath, containers: CustomViewAttributeContainer[]): void => {
		const { omitContainerCallback } = this.store.options;
		const { depth, endClass } = extendedPath;
		
		if (!endClass.abstract) {
			return;
		}
		
		// const { pathToSource } = this.specBuilder;
		// const path = depth ? extendedPath : pathToSource;
		// const lastRelation = path?.endDomainRelation;
		// let derivedClasses: ReflectionClass[];

		// if (pathToSource && !lastRelation) {
		// 	// TODO - need to somehow go back to source and get all domain destinations using standard path
		// 	// if abstract class encountered, then get all domain destinations and then add next step in path;
		// 	// so the path forks into more paths as it is traversed
		// 	derivedClasses = endClass.concreteClasses;
		// } else if (lastRelation) {
		// 	const relation = lastRelation as ReflectionDomainRelation;
		// 	derivedClasses = relation.destinations.map((dc) => getClass(dc.type));
		// } else {
		// 	derivedClasses = endClass.concreteClasses;
		// }
		
		endClass.concreteClasses.forEach((rc) => {
			if (rc.disabled) {
				return;
			}

			const derivedPath = extendedPath.clone().setEndType(rc.type);
			if (derivedPath.isOmitted || omitContainerCallback?.(derivedPath)) {
				return;
			}
			
			containers.push(new CustomViewAttributeContainer(this, derivedPath, endClass));
		});
	};

	/** initialize all containers in view */
	#initializeContainers = (): void => {
		const { containers, isMainLevel, sourceMatrixContainer, specBuilder, store } = this;
		const { additionalPreferencePaths, configuredMatrixGroupSpecs, configuredPaths, defaultPathHints, defaultSourceClass } = specBuilder;
		const { getContainer, options, registerContainer } = store;
		const { noGroupSupport, noMatrixSupport, omitContainerCallback, omitDerivedClasses } = options;
		const sourcePath = sourceMatrixContainer?.extendedPath ?? ExtendedPath.create(defaultSourceClass, null, defaultPathHints);
		const configuredPathsForLevel = sourceMatrixContainer?.sourceMatrixGroupSpec.configuredSubHeadingPaths ?? configuredPaths;
		const otherPaths = getCustomViewAvailablePaths(sourcePath, additionalPreferencePaths, configuredPathsForLevel, !isMainLevel);

		// populate optionalAttributesMaps from any paths to LookupItem
		const maps = {};

		populateCustomViewOptionalAttributesMaps(maps, otherPaths);

		if (!isEmpty(maps)) {
			options.optionalAttributesMaps ||= { ...maps, ...options.optionalAttributesMaps };
		}

		// create containers
		const existingSourceContainer = sourceMatrixContainer ?? getContainer(sourcePath);
		const matrixGroupId = sourceMatrixContainer?.matrixGroupId;
		const sourceContainer = existingSourceContainer ?? new CustomViewAttributeContainer(this, sourcePath, null, null, true);
		const newPaths = otherPaths.filter((path) => !getContainer(path, matrixGroupId) && !omitContainerCallback?.(path));
		const newContainers = newPaths.map((path) => new CustomViewAttributeContainer(this, path));

		if (!omitDerivedClasses) {
			// add derived containers for any paths that lead to an abstract class
			if (sourceMatrixContainer || !existingSourceContainer) {
				this.#addDerivedContainers(sourcePath, newContainers);
			}

			newPaths.forEach((path) => {
				this.#addDerivedContainers(path, newContainers);
			});
		}

		// create matrix containers if supported, and not already within a matrix group (matrix groups only work at this time if groups are also supported)
		if (isMainLevel && !sourceMatrixContainer && !noMatrixSupport && !noGroupSupport) {
			configuredMatrixGroupSpecs.forEach((spec) => {
				if (!spec.matrixExtendedPath.isOmitted) {
					newContainers.push(new CustomViewAttributeContainer(this, spec.matrixExtendedPath, null, spec, true));
				}
			});
		}

		// sort and add containers
		const sorted = sortBy(newContainers, ({ name }) => name);

		for (const c of [sourceContainer, ...sorted]) {
			containers.push(c);
			registerContainer(c);
		};
	};

	constructor(
		store: CustomViewAttributesStore,
		name: string,
		specBuilder: SpecBuilder,
		duplicateLevel: boolean,
		isMainLevel: boolean,
		sourceMatrixContainer?: CustomViewAttributeContainer,
	) {
		makeObservable(this);
		this.duplicateLevel = duplicateLevel;
		this.isMainLevel = isMainLevel;
		this.name = name;
		this.specBuilder = specBuilder;
		this.store = store;
		this.sourceMatrixContainer = sourceMatrixContainer;

		// FIX: do we need to populate (all) containers if duplicate?
		// if we are just concerned about attributes to select, then no
		// but if we are also concerned about data management (filters), then maybe so
		if (!duplicateLevel) {
			this.#initializeContainers();
		}
	}
};
